import React, { useMemo, useState, useEffect } from 'react';
import { 
  BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  ShieldCheck, ChevronLeft, Loader2, Brain, Sparkles, Download, Activity, 
  Sliders, RefreshCw, Briefcase, HeartPulse, AlertTriangle, CheckSquare, Award, Send, Lock, TrendingUp, Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../core/firebase';
import { useAppContext } from '../../core/App';

// Initialize Cloud Functions
const generateTalentReportInsights = httpsCallable(functions, 'generateTalentReportInsights');
const initiateBlindPing = httpsCallable(functions, 'initiateBlindPing');
const getRecruiterAnalytics = httpsCallable(functions, 'getRecruiterAnalytics'); 

export default function EmployerTalentPortal({ onBack }) {
  const { schoolConfig, setToastMessage, userProfile } = useAppContext();
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [isExportingCert, setIsExportingCert] = useState(false);
  const [isPinging, setIsPinging] = useState(null);
  
  // --- STATE MACHINE FOR PROVENANCE HASH ---
  const [verificationState, setVerificationState] = useState('idle'); 
  
  // --- TOGGLES & FILTERS ---
  const [isSimMode, setIsSimMode] = useState(true);
  const [dashboardView, setDashboardView] = useState('CAREER'); 
  const [traitFilter, setTraitFilter] = useState([]);

  // --- 1. FETCH LIVE DATA ---
  useEffect(() => { 
      let isMounted = true;

      const fetchCohortData = async () => {
          // 1. Safety check: Ensure the user is an auditor and has a specific tenant ID
          if (!userProfile?.enterpriseTenantId || userProfile.enterpriseTenantId === 'public') {
              console.warn("Access Denied: Not assigned to an enterprise tenant.");
              return;
          }

          setIsLoadingLive(true);
          try {
              // 2. Fetch directly from the zero-trust backend endpoint
              const result = await getRecruiterAnalytics();
              
              // 3. Set your dashboard state with the securely generated payload
              if (isMounted) {
                  setDashboardData(result.data);
              }

          } catch (error) {
              console.error("Enterprise fetch failed:", error);
          } finally { 
              if (isMounted) {
                  setIsLoadingLive(false); 
              }
          }
      };

      fetchCohortData();

      return () => {
          isMounted = false;
      };
  }, [dashboardView, userProfile?.enterpriseTenantId]);

  // --- 2. DATA PROCESSING WITH LEXICON SHIFT ---
  const activeData = useMemo(() => {
    if (isSimMode) {
      return {
        poolSize: 500,
        aggregateMtbf: 1250, 
        adaptabilityIndex: 88,
        logisticalHeatmap: [
            { name: 'Tier 1 City', survival_rate: 85, deadlock_fail: 15 }, 
            { name: 'Tier 2 City', survival_rate: 92, deadlock_fail: 8 },
            { name: 'Rural/Remote', survival_rate: 78, deadlock_fail: 22 },
        ],
        candidates: [
            { hash: '0x1A4B9c2F...', verifiedTraits: ['Secure Enterprise Data Hygiene'], mtbf: 980, adaptabilityScore: 92, volatilityIndex: 12 },
            { hash: '0x8F2C1a5E...', verifiedTraits: ['High Ambiguity Tolerance'], mtbf: 1450, adaptabilityScore: 85, volatilityIndex: 45 },
            { hash: '0x3D9E4b7D...', verifiedTraits: ['Secure Enterprise Data Hygiene', 'High Ambiguity Tolerance'], mtbf: 820, adaptabilityScore: 96, volatilityIndex: 8 }
        ]
      };
    }

    // NEW: The backend now serves the perfectly formatted payload. No client-side math required.
    const safeLive = dashboardData || { candidates: [], poolSize: 0, aggregateMtbf: 0, adaptabilityIndex: 0 };
    
    return {
      poolSize: safeLive.poolSize || safeLive.candidates?.length || 0,
      aggregateMtbf: safeLive.aggregateMtbf || 0,
      adaptabilityIndex: safeLive.adaptabilityIndex || 85,
      logisticalHeatmap: [], 
      candidates: safeLive.candidates || []
    };
  }, [isSimMode, dashboardData]);

  const toggleFilter = (trait) => {
      setTraitFilter(prev => prev.includes(trait) ? prev.filter(t => t !== trait) : [...prev, trait]);
  };

  const filteredCandidates = activeData.candidates.filter(c => {
      if (traitFilter.length === 0) return true;
      return traitFilter.every(t => c.verifiedTraits.includes(t));
  });

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    setAiInsights(null);
    try {
      const result = await generateTalentReportInsights({
          metrics: activeData,
          selectedSections: [dashboardView]
      });
      setAiInsights(result.data);
    } catch (e) { 
        console.error("AI Consultant Failed:", e);
        alert("Failed to generate strategic briefing. Ensure functions are deployed.");
    } finally { 
        setIsGenerating(false); 
    }
  };

  const handleBlindPing = async (hash) => {
      setIsPinging(hash);
      try {
          await initiateBlindPing({ candidateHash: hash });
          setToastMessage(`Blind Ping Dispatched to ${hash.substring(0,8)}...`);
      } catch (err) {
          alert(`Failed to dispatch ping: ${err.message}`);
      } finally {
          setIsPinging(null);
      }
  };

  // MTBF Gauge Data
  const mtbfData = [
      { name: 'MTBF', value: Math.min(activeData.aggregateMtbf, 5000) },
      { name: 'Remaining', value: Math.max(0, 5000 - activeData.aggregateMtbf) }
  ];
  const COLORS = ['#3b82f6', '#f1f5f9'];

  return (
    <div className="max-w-7xl mx-auto p-6 animate-fade-in pb-20 bg-slate-50 min-h-screen">
      
      {/* HEADER CONTROLS */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold"><ChevronLeft size={20} /> Exit Portal</button>
        
        <div className="flex items-center gap-3 bg-white p-1.5 rounded-lg border border-slate-200 shadow-sm">
            <button 
                onClick={() => { setDashboardView('CAREER'); setAiInsights(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${dashboardView === 'CAREER' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <Briefcase size={14} /> Verified Talent Pool
            </button>
            <button 
                onClick={() => { setDashboardView('RETENTION'); setAiInsights(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${dashboardView === 'RETENTION' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <HeartPulse size={14} /> Flight-Risk & Burnout
            </button>
            <button 
                onClick={() => { setDashboardView('ACCREDITATION'); setAiInsights(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${dashboardView === 'ACCREDITATION' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <Award size={14} /> EEOC Bias Audit Heatmap
            </button>
        </div>

        <div className="flex items-center gap-3">
            <button onClick={() => { setIsSimMode(!isSimMode); setAiInsights(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${isSimMode ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 border'}`}>
              <Sliders size={14} /> {isSimMode ? "Sim Data" : "Live Data"}
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: METRICS */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Verified Candidates</h3>
                    <div className="text-5xl font-black text-slate-900">{activeData.poolSize}</div>
                </div>

                {dashboardView === 'CAREER' ? (
                    <>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 col-span-2">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Aggregate MTBF (Mean Time Between Failures)</h3>
                            <div className="flex items-center gap-4">
                                <div className="text-4xl font-black text-blue-600">{activeData.aggregateMtbf}ms</div>
                                <div className="h-16 w-32">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={mtbfData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={20} outerRadius={30} paddingAngle={0} dataKey="value" stroke="none">
                                                {mtbfData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </>
                ) : dashboardView === 'RETENTION' ? (
                    <>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Adaptability Index</h3>
                            <div className={`text-5xl font-black ${activeData.adaptabilityIndex < 50 ? 'text-red-500' : 'text-emerald-600'}`}>{activeData.adaptabilityIndex}%</div>
                            <p className="text-xs text-slate-500">Logistical Strain Tolerance</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Flight Risk Flags</h3>
                            <div className="text-5xl font-black text-orange-500">
                                {activeData.logisticalHeatmap.reduce((acc, curr) => acc + curr.deadlock_fail, 0) || 45}
                            </div>
                            <p className="text-xs text-slate-500">Candidates Nearing Burnout</p>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Bias Audits Passed</h3>
                            <div className="text-5xl font-black text-indigo-600">100%</div>
                            <p className="text-xs text-slate-500">EEOC Framework</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Untimed Evaluations</h3>
                            <div className="text-5xl font-black text-purple-600">Active</div>
                            <p className="text-xs text-slate-500">ADA Compliant Flow</p>
                        </div>
                    </>
                )}
            </div>

            {/* THE INCLUSIONARY FILTER & MARKETPLACE */}
            {dashboardView === 'CAREER' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Send className="text-blue-500"/> Zero-Knowledge Blind Ping</h3>
                    
                    <div className="flex flex-wrap gap-2 mb-6 border-b pb-4">
                        <button onClick={() => toggleFilter('Secure Enterprise Data Hygiene')} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${traitFilter.includes('Secure Enterprise Data Hygiene') ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                            Verified: Secure Enterprise Data Hygiene
                        </button>
                        <button onClick={() => toggleFilter('High Ambiguity Tolerance')} className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${traitFilter.includes('High Ambiguity Tolerance') ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                            Verified: High Ambiguity Tolerance
                        </button>
                    </div>

                    <div className="space-y-3">
                        {filteredCandidates.length === 0 ? (
                            <p className="text-slate-400 text-sm italic py-4">No candidates match the selected cryptographic traits.</p>
                        ) : (
                            filteredCandidates.map(c => (
                                <div key={c.hash} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 shadow-sm gap-4 hover:border-blue-300 transition-colors">
                                    <div>
                                        <p className="font-mono text-sm text-slate-600 font-bold flex items-center gap-2">
                                            <Lock size={14} className="text-slate-400" /> {c.hash}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {c.verifiedTraits.map(t => (
                                                <span key={t} className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded font-bold text-indigo-600 shadow-sm">{t}</span>
                                            ))}
                                            <span className="text-[10px] bg-slate-800 text-white px-2 py-0.5 rounded font-bold shadow-sm">MTBF: {c.mtbf}ms</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold shadow-sm ${c.volatilityIndex > 25 ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}`}>
                                                CVI: {c.volatilityIndex} {c.volatilityIndex > 25 ? '(Erratic)' : '(Stable)'}
                                            </span>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleBlindPing(c.hash)} 
                                        disabled={isPinging === c.hash} 
                                        className="w-full sm:w-auto px-5 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl shadow-lg hover:bg-black transition-transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {isPinging === c.hash ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                                        {isPinging === c.hash ? 'Pinging...' : 'Request Connection'}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {dashboardView === 'RETENTION' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 min-h-[400px]">
                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><Activity className="text-red-500"/> Logistical Adaptability Index</h3>
                    <div className="h-80 w-full">
                         <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={activeData.logisticalHeatmap} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} />
                                <Tooltip cursor={{fill: '#f8fafc'}} />
                                <Bar dataKey="survival_rate" name="Stabilized Logistics" stackId="a" fill="#10b981" />
                                <Bar dataKey="deadlock_fail" name="Mathematical Deadlock Risk" stackId="a" fill="#ef4444" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* --- ACCREDITATION COMPLIANCE PANEL --- */}
            {dashboardView === 'ACCREDITATION' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 animate-fade-in space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                                    <Award className="text-indigo-600" /> EEOC & ADA Compliance Ledger
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">Validating Untimed SJT Delivery and Algorithmic Bias Mitigation.</p>
                            </div>

                            <AnimatePresence mode="wait">
                                {verificationState !== 'idle' && (
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0.8, opacity: 0 }}
                                        className={`px-3 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-sm ${
                                            verificationState === 'verifying' ? 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse' :
                                            verificationState === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 shadow-[0_0_10px_rgba(16,185,129,0.2)]' :
                                            'bg-red-50 text-red-700 border-red-200'
                                        }`}
                                    >
                                        {verificationState === 'verifying' && <Loader2 className="animate-spin" size={12} />}
                                        {verificationState === 'verified' && <ShieldCheck size={12} className="text-emerald-600 fill-emerald-100" />}
                                        {verificationState === 'failed' && <AlertTriangle size={12} className="text-red-600" />}
                                        <span>Provenance: {verificationState}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        <button 
                            onClick={async () => {
                                setIsExportingCert(true);
                                setVerificationState('verifying');
                                try {
                                    const generateCert = httpsCallable(functions, 'generateCandidateProvenanceToken');
                                    const res = await generateCert({ candidateHash: 'SYSTEM_AUDIT_HASH' });
                                    setVerificationState('verified');
                                    setToastMessage("Cryptographic provenance key locked successfully.");
                                    alert(`PROVENANCE SIGNATURE SECURED:\n\nHash: ${res.data.provenanceToken}\n\nData origin integrity verified server-side.`);
                                } catch (err) {
                                    setVerificationState('failed');
                                    alert(`Provenance check rejected: ${err.message}`);
                                } finally {
                                    setIsExportingCert(false);
                                }
                            }}
                            disabled={isExportingCert || activeData.poolSize === 0}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-md transition-all active:scale-95 disabled:opacity-50"
                        >
                            {isExportingCert ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                            {isExportingCert ? "Signing Token..." : "Verify Audit Hash"}
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="bg-slate-900 text-white px-4 py-3 text-xs font-black uppercase tracking-wider flex justify-between">
                                <span>ADA Compliance: Untimed Situational Judgment (SJT)</span>
                                <span className="text-emerald-400 font-bold">100% Passed</span>
                            </div>
                            <div className="p-4 bg-white divide-y space-y-3 text-xs text-slate-600">
                                <div className="pt-2 flex justify-between items-start">
                                    <div className="max-w-[75%]">
                                        <span className="font-bold text-slate-800 block">Metric Node: Forced Delay Removal</span>
                                        <span className="italic">Telemetry extracted from: DynamicSJTGame.js (Countdown Clocks Disabled)</span>
                                    </div>
                                    <span className="font-mono bg-slate-100 px-2 py-0.5 rounded border font-bold text-slate-700">Verified</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* RIGHT COLUMN: AI CONSULTANT (THE RISK ASSESSMENT BRIEFING) */}
        <div className="lg:col-span-5 xl:col-span-4 flex flex-col h-[700px] lg:h-auto">
            <div className="bg-slate-900 text-white p-6 md:p-8 rounded-3xl shadow-2xl flex-1 flex flex-col relative overflow-hidden border border-slate-700">
                 {/* Background decoration */}
                 <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none"><Brain size={150} /></div>

                 <h2 className="text-2xl font-black uppercase mb-2 flex items-center gap-2 relative z-10 tracking-tight">
                    <Brain className="text-emerald-400"/> Operational Risk Briefing
                 </h2>

                 {!aiInsights ? (
                     <div className="flex-1 flex flex-col items-center justify-center text-center relative z-10 py-10">
                         <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                            Synthesize aggregate telemetry and MTBF data into a predictive risk briefing for corporate recruiting teams.
                         </p>
                         <button
                            onClick={handleGenerateReport}
                            disabled={isGenerating}
                            className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-wider rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:scale-100"
                         >
                            {isGenerating ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
                            {isGenerating ? "Synthesizing..." : "Generate Briefing"}
                         </button>
                     </div>
                 ) : (
                     <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar relative z-10 space-y-6 mt-4 pb-4">
                         
                         {/* 1. EXECUTIVE SUMMARY */}
                         <div className="space-y-4">
                             <div className="flex items-center justify-between">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Executive Summary</h3>
                                <span className="px-2 py-1 bg-white/10 rounded text-[10px] font-bold text-emerald-400 border border-emerald-500/30 shadow-sm">
                                    Vitality: {aiInsights.executive_health_summary?.vitality_score || "N/A"}
                                </span>
                             </div>
                             <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-500/20 border border-red-500/50 text-red-300 text-[10px] font-black uppercase rounded-full shadow-inner">
                                <AlertTriangle size={12} /> {aiInsights.executive_health_summary?.primary_risk_vector || "Risk Vector Unknown"}
                             </div>
                             <p className="text-sm text-slate-200 leading-relaxed font-medium">
                                {aiInsights.executive_health_summary?.summary}
                             </p>
                         </div>

                         {/* 2. COGNITIVE ENDURANCE */}
                         {aiInsights.cognitive_endurance_audit && (
                             <div className="p-5 bg-white/5 border border-white/10 rounded-2xl shadow-inner">
                                 <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2"><Activity size={14} /> Cognitive Endurance</h3>
                                 <div className="grid grid-cols-2 gap-3 mb-4">
                                     <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">MTBF Integrity</div>
                                        <div className="font-mono text-sm text-slate-200 font-bold">{aiInsights.cognitive_endurance_audit?.recovery_latency}</div>
                                     </div>
                                     <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Frustration Spike</div>
                                        <div className="font-mono text-sm text-slate-200 font-bold">{aiInsights.cognitive_endurance_audit?.frustration_index_spike}</div>
                                     </div>
                                 </div>
                                 <div className="border-l-2 border-blue-500 pl-3 py-1 bg-blue-500/5 rounded-r-lg">
                                     <p className="text-xs text-slate-300 italic">"{aiInsights.cognitive_endurance_audit?.mentor_insight}"</p>
                                 </div>
                             </div>
                         )}

                         {/* 3. FINANCIAL ANXIETY */}
                         {aiInsights.financial_anxiety_index && (
                             <div className="p-5 bg-white/5 border border-white/10 rounded-2xl shadow-inner">
                                 <h3 className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-4 flex items-center gap-2"><TrendingUp size={14} /> Financial Anxiety</h3>
                                 <div className="grid grid-cols-2 gap-3 mb-4">
                                     <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Redline Freq.</div>
                                        <div className="font-mono text-sm text-slate-200 font-bold">{aiInsights.financial_anxiety_index?.redline_frequency}</div>
                                     </div>
                                     <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Market Lock</div>
                                        <div className="font-mono text-xs text-slate-200 leading-snug line-clamp-2" title={aiInsights.financial_anxiety_index?.market_lock_constraints}>{aiInsights.financial_anxiety_index?.market_lock_constraints}</div>
                                     </div>
                                 </div>
                                 <div className="border-l-2 border-orange-500 pl-3 py-1 bg-orange-500/5 rounded-r-lg">
                                     <p className="text-xs text-slate-300 italic">"{aiInsights.financial_anxiety_index?.mentor_insight}"</p>
                                 </div>
                             </div>
                         )}

                         {/* 4. INSTITUTIONAL DIRECTIVES */}
                         {aiInsights.institutional_directives && (
                             <div>
                                 <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-2"><ShieldCheck size={14} /> Directives</h3>
                                 <div className="space-y-3">
                                     <div className="flex items-start gap-3 bg-black/20 p-4 rounded-xl border border-white/5 shadow-inner">
                                         <CheckSquare size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                                         <div>
                                             <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Wellness Deployment</div>
                                             <div className="text-xs text-slate-200 leading-snug">{aiInsights.institutional_directives?.wellness_deployment}</div>
                                         </div>
                                     </div>
                                     <div className="flex items-start gap-3 bg-black/20 p-4 rounded-xl border border-white/5 shadow-inner">
                                         <CheckSquare size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                                         <div>
                                             <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Curriculum Calibration</div>
                                             <div className="text-xs text-slate-200 leading-snug">{aiInsights.institutional_directives?.curriculum_calibration}</div>
                                         </div>
                                     </div>
                                     <div className="flex items-start gap-3 bg-black/20 p-4 rounded-xl border border-white/5 shadow-inner">
                                         <CheckSquare size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                                         <div>
                                             <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Integrity Reinforcement</div>
                                             <div className="text-xs text-slate-200 leading-snug">{aiInsights.institutional_directives?.integrity_reinforcement}</div>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                         )}

                         {/* Reset Button */}
                         <button onClick={() => setAiInsights(null)} className="w-full py-3 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-widest mt-4 flex items-center justify-center gap-2">
                            <RefreshCw size={14} /> Clear Briefing
                         </button>
                     </div>
                 )}
            </div>
        </div>

      </div>
    </div>
  );
}